import type {
  CodecImplementationEvidence, DecodeObservation, FramePresentationEvidence, FrameTimingSource,
  MeasurementFreshness, NetworkPathEpoch, RecoveryActionEvent, StreamRecoveryEvidence, SelectedNetworkPath,
} from './contracts';
import type {
  MobileTierDecision, PlaybackCapacityDecision, PlaybackCapacityEvidence,
  ResolutionChangeRequest, ResolutionChangeStatus, ResolutionObservation,
} from './qualityContracts';

type Fields = Record<string, unknown>;
const object = (v: unknown): v is Fields => v !== null && typeof v === 'object' && !Array.isArray(v);
const number = (v: unknown): v is number => typeof v === 'number' && Number.isFinite(v) && v >= 0;
const integer = (v: unknown): v is number => number(v) && Number.isSafeInteger(v);
const text = (v: unknown, max = 160): v is string => typeof v === 'string' && v.length > 0 && v.length <= max;
const oneOf = (v: unknown, choices: readonly string[]): boolean => typeof v === 'string' && choices.includes(v);
const optional = (v: unknown, guard: (v: unknown) => boolean): boolean => v === undefined || guard(v);
const bool = (v: unknown): v is boolean => typeof v === 'boolean';
const exact = (v: Fields, keys: readonly string[]): boolean => Object.keys(v).every(k => keys.includes(k));
const tier = (v: unknown): boolean => oneOf(v, ['top', 'middle', 'bottom']);
const visibility = (v: unknown): boolean => oneOf(v, ['visible', 'hidden', 'unknown']);
const playback = (v: unknown): boolean => oneOf(v, ['playing', 'paused', 'ended', 'unknown']);
const device = (v: unknown): boolean => oneOf(v, ['mobile', 'desktop', 'unknown']);

export function isFrameTimingSource(value: unknown): value is FrameTimingSource {
  return oneOf(value, ['presentation-time', 'callback-time', 'unavailable']);
}

export function isMeasurementFreshness(value: unknown): value is MeasurementFreshness {
  if (!object(value) || !exact(value, ['state', 'ageMs', 'sequence'])) return false;
  if (!oneOf(value['state'], ['fresh', 'stale', 'unavailable'])) return false;
  if (value['state'] === 'unavailable') return value['ageMs'] === undefined && value['sequence'] === undefined;
  return optional(value['ageMs'], number) && optional(value['sequence'], integer);
}

/** Path identifiers stay local; wire evidence only exposes a numeric epoch. */
export function isSelectedNetworkPath(value: unknown): value is SelectedNetworkPath {
  if (!object(value) || !exact(value, ['selection', 'transportId', 'selectedPairId', 'rttMs', 'relay'])) return false;
  if (value['selection'] === 'unavailable') return Object.keys(value).every(k => k === 'selection');
  return value['selection'] === 'selected' && optional(value['transportId'], v => text(v, 128))
    && text(value['selectedPairId'], 128) && optional(value['rttMs'], number) && optional(value['relay'], bool);
}

export function isNetworkPathEpoch(value: unknown): value is NetworkPathEpoch {
  if (!object(value) || !exact(value, ['epoch', 'selection', 'reason']) || !integer(value['epoch'])) return false;
  if (value['selection'] === 'unavailable') return value['reason'] === 'unavailable';
  return value['selection'] === 'selected' && oneOf(value['reason'], ['initial', 'selected-pair-change']);
}

export function isFramePresentationEvidence(value: unknown): value is FramePresentationEvidence {
  if (!object(value) || !exact(value, ['visibility', 'playback', 'progressSequence', 'lastPresentationAgeMs',
    'callbackAgeMs', 'decodedFrames', 'decodeObservation', 'presentedFrames', 'playbackTotalFrames', 'playbackDroppedFrames', 'timingSource'])) return false;
  if (!visibility(value['visibility']) || !playback(value['playback']) || !integer(value['progressSequence'])) return false;
  if (!isFrameTimingSource(value['timingSource'])) return false;
  if (value['decodeObservation'] !== undefined
    && (!integer(value['decodedFrames']) || !isDecodeObservation(value['decodeObservation']))) return false;
  if (!['lastPresentationAgeMs', 'callbackAgeMs'].every(k => optional(value[k], number))) return false;
  if (!['decodedFrames', 'presentedFrames', 'playbackTotalFrames', 'playbackDroppedFrames']
    .every(k => optional(value[k], integer))) return false;
  return !(number(value['playbackDroppedFrames']) && number(value['playbackTotalFrames'])
    && value['playbackDroppedFrames'] > value['playbackTotalFrames']);
}

export function isDecodeObservation(value: unknown): value is DecodeObservation {
  return object(value) && exact(value, ['sequence', 'ageMs'])
    && integer(value['sequence']) && number(value['ageMs']);
}

export function isCodecImplementationEvidence(value: unknown): value is CodecImplementationEvidence {
  return object(value) && exact(value, ['implementation', 'kind', 'powerEfficient'])
    && oneOf(value['kind'], ['hardware', 'software', 'unknown'])
    && optional(value['implementation'], v => text(v, 128)) && optional(value['powerEfficient'], bool);
}

/** Strict, bounded extension guard. Unknown fields cannot smuggle path addresses into logs. */
export function isStreamRecoveryEvidence(value: unknown): value is StreamRecoveryEvidence {
  return object(value) && exact(value, ['version', 'deviceClass', 'path', 'presentation', 'latency', 'encoder', 'decoder'])
    && value['version'] === 1 && optional(value['deviceClass'], device)
    && optional(value['path'], isNetworkPathEpoch) && optional(value['presentation'], isFramePresentationEvidence)
    && optional(value['latency'], isMeasurementFreshness)
    && optional(value['encoder'], isCodecImplementationEvidence) && optional(value['decoder'], isCodecImplementationEvidence);
}

export function isRecoveryActionEvent(value: unknown): value is RecoveryActionEvent {
  return object(value) && exact(value, ['episodeId', 'pathEpoch', 'owner', 'action', 'outcome', 'elapsedMs', 'reason'])
    && text(value['episodeId']) && integer(value['pathEpoch']) && number(value['elapsedMs']) && text(value['reason'])
    && oneOf(value['owner'], ['handoff', 'transport', 'presentation', 'buffer-drain'])
    && oneOf(value['action'], ['protect-bitrate', 'release-protection', 'request-keyframe', 'refresh-connection', 'drain', 'demote-tier'])
    && oneOf(value['outcome'], ['requested', 'applied', 'not-applied', 'recovered', 'exhausted']);
}

export function isMobileTierDecision(value: unknown): value is MobileTierDecision {
  return object(value) && exact(value, ['device', 'tier', 'maxWidth', 'maxHeight', 'reason'])
    && device(value['device']) && tier(value['tier'])
    && integer(value['maxWidth']) && value['maxWidth'] > 0 && integer(value['maxHeight']) && value['maxHeight'] > 0
    && oneOf(value['reason'], ['mobile', 'constrained', 'desktop', 'unknown-safe', 'retained-floor']);
}

export function isPlaybackCapacityEvidence(value: unknown): value is PlaybackCapacityEvidence {
  if (!object(value) || !exact(value, ['peerGeneration', 'pathEpoch', 'sequence', 'sampledAtMs', 'freshness',
    'visibility', 'playback', 'currentTier', 'configuredFps', 'sourceActive', 'presentedFps', 'freezeDelta',
    'freezeDurationDeltaMs', 'ongoingPresentationStallMs', 'intervalMs', 'receivedVideoBitrateBps', 'senderTargetBitrateBps', 'lossPct',
    'standingQueueMs', 'bandwidthLimited', 'hostEvidenceFresh', 'bitrateProtectionApplied'])) return false;
  if (!['peerGeneration', 'pathEpoch', 'sequence'].every(k => integer(value[k]))) return false;
  if (!['sampledAtMs', 'configuredFps', 'intervalMs'].every(k => number(value[k]))) return false;
  if (!number(value['configuredFps']) || value['configuredFps'] === 0 || !number(value['intervalMs']) || value['intervalMs'] === 0) return false;
  if (!isMeasurementFreshness(value['freshness']) || !visibility(value['visibility']) || !playback(value['playback'])) return false;
  if (!tier(value['currentTier']) || !optional(value['sourceActive'], bool) || !optional(value['bandwidthLimited'], bool)) return false;
  if (!bool(value['hostEvidenceFresh']) || !bool(value['bitrateProtectionApplied'])) return false;
  if (!optional(value['freezeDelta'], integer)) return false;
  if (!['presentedFps', 'freezeDurationDeltaMs', 'ongoingPresentationStallMs', 'receivedVideoBitrateBps', 'senderTargetBitrateBps', 'standingQueueMs']
    .every(k => optional(value[k], number))) return false;
  return optional(value['lossPct'], v => number(v) && v <= 100);
}

export function isPlaybackCapacityDecision(value: unknown): value is PlaybackCapacityDecision {
  return object(value) && exact(value, ['state', 'episodeId', 'qualifyingMs', 'requestTier', 'reason'])
    && oneOf(value['state'], ['healthy', 'unknown', 'suspended', 'qualifying', 'demote', 'at-bottom'])
    && optional(value['episodeId'], text) && number(value['qualifyingMs']) && text(value['reason'])
    && (value['state'] === 'demote' ? value['requestTier'] === 'bottom' : value['requestTier'] === undefined);
}

export function isResolutionChangeRequest(value: unknown): value is ResolutionChangeRequest {
  return object(value) && exact(value, ['requestId', 'peerGeneration', 'pathEpoch', 'maxWidth', 'maxHeight', 'requestedAtMs', 'reason'])
    && text(value['requestId']) && integer(value['peerGeneration']) && integer(value['pathEpoch'])
    && integer(value['maxWidth']) && value['maxWidth'] > 0 && integer(value['maxHeight']) && value['maxHeight'] > 0
    && number(value['requestedAtMs']) && oneOf(value['reason'], ['initial-mobile', 'reconnect-mobile', 'sustained-poor-playback']);
}

export function isResolutionObservation(value: unknown): value is ResolutionObservation {
  return object(value) && exact(value, ['peerGeneration', 'pathEpoch', 'source', 'width', 'height', 'observationSequence', 'freshness', 'observedAfterRequest'])
    && integer(value['peerGeneration']) && integer(value['pathEpoch']) && integer(value['observationSequence'])
    && integer(value['width']) && value['width'] > 0 && integer(value['height']) && value['height'] > 0
    && oneOf(value['source'], ['capture', 'sender', 'receiver']) && isMeasurementFreshness(value['freshness'])
    && bool(value['observedAfterRequest']);
}

export function isResolutionChangeStatus(value: unknown): value is ResolutionChangeStatus {
  return object(value) && exact(value, ['requestId', 'stage', 'elapsedMs', 'reason'])
    && text(value['requestId']) && number(value['elapsedMs']) && optional(value['reason'], text)
    && oneOf(value['stage'], ['requested', 'host-applied', 'sender-verified', 'receiver-verified',
      'host-timeout', 'receiver-unverified', 'failed', 'superseded']);
}

/** Optional timing extension is atomic: malformed fields never partially certify freshness. */
export function hasValidFrameObservation(value: Fields): boolean {
  return optional(value['observationSequence'], integer) && optional(value['observationAgeMs'], number)
    && optional(value['pathEpoch'], integer) && optional(value['timingSource'], isFrameTimingSource);
}

/** Preserve valid legacy frames while discarding an unsupported timing extension. */
export function withoutInvalidFrameObservation<T extends Fields>(value: T): T {
  if (hasValidFrameObservation(value)) return value;
  const cleaned = { ...value };
  delete cleaned['observationSequence'];
  delete cleaned['observationAgeMs'];
  delete cleaned['pathEpoch'];
  delete cleaned['timingSource'];
  return cleaned;
}
